import { Button, Form, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFax } from '@fortawesome/free-solid-svg-icons';
import { displayPhoneNumber } from '../displayPhoneNumber';

interface FaxSettingsViewProps {
  fax: string;
  isFaxEnabled: boolean;
  faxVerified: boolean;
  verificationStatus: 'idle' | 'pending' | 'success' | 'error';
  verificationMessage: string;
  verificationCode: string;
  onVerificationCodeChange: (val: string) => void;
  onSubmitVerification: () => void;
  onResendCode: () => void;
}

export default function FaxSettingsView({
  fax,
  isFaxEnabled,
  faxVerified,
  verificationStatus,
  verificationMessage,
  verificationCode,
  onVerificationCodeChange,
  onSubmitVerification,
  onResendCode,
}: FaxSettingsViewProps) {
  return (
    <div className='fax-section'>
      <div className='fax-header'>
        <FontAwesomeIcon icon={faFax} className='text-secondary' />
        <h4>Fax settings</h4>
      </div>

      <div className='details-grid mb-3'>
        <div className='detail-item'>
          <span className='label'>Fax number</span>
          <span className='value'>{displayPhoneNumber(fax)}</span>
        </div>
        <div className='detail-item'>
          <span className='label'>Configurations</span>
          <div className='d-flex gap-3'>
            <Badge bg={isFaxEnabled ? 'success' : 'secondary'}>
              {isFaxEnabled ? 'Auto-Fax Enabled' : 'Auto-Fax Disabled'}
            </Badge>
          </div>
        </div>
        {isFaxEnabled && (
          <div className='detail-item'>
            <span className='label'>Status</span>
            <div className='d-flex align-items-center'>
              {faxVerified ? (
                <Badge bg='success'>Verified</Badge>
              ) : verificationStatus === 'error' ? (
                <Badge bg='danger'>Failed</Badge>
              ) : (
                <Badge bg='warning' text='dark'>
                  Pending
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>

      {isFaxEnabled && !faxVerified && (
        <div className='verification-box bg-light p-3 rounded'>
          <h6 className='fw-bold mb-2'>Fax Verification</h6>
          <p className='small text-muted mb-2'>
            Enter the code sent to the fax number above. The code may take up to 5 minutes to arrive.
          </p>
          <div className='verification-input-group'>
            <Form.Control
              type='text'
              maxLength={6}
              placeholder='123456'
              value={verificationCode}
              onChange={e => onVerificationCodeChange(e.target.value)}
              disabled={verificationStatus === 'pending'}
            />
            <Button
              size='sm'
              variant='primary'
              onClick={onSubmitVerification}
              disabled={verificationCode.length !== 6 || verificationStatus === 'pending'}
            >
              {verificationStatus === 'pending' ? <Spinner size='sm' animation='border' /> : 'Confirm'}
            </Button>
            <Button
              size='sm'
              variant='outline-secondary'
              onClick={onResendCode}
              disabled={verificationStatus === 'pending'}
            >
              Resend
            </Button>
          </div>
          {verificationMessage && (
            <div className={`mt-2 small ${verificationStatus === 'success' ? 'text-success' : 'text-danger'}`}>
              {verificationMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
